import * as yup from "yup";

import { BILLING_METHOD } from "../utils/onboarding-helper";
import { onboardingPropertyDraftSchema } from "./onboarding-properties.schema";

const corporateContactSchema = yup.object({
  first_name: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Corporate Contact First Name")
    .required(),
  last_name: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Corporate Contact Last Name")
    .required(),
  phone_number: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .matches(/^\d{10}$/, "Must be a 10-digit number")
    .label("Corporate Contact Phone Number")
    .required(),
  email: yup
    .string()
    .email()
    .label("Corporate Contact Email")
    .lowercase("Please Enter Lowercase Letters")
    .required()
    .strict(),
});

const corporateContactCreateSchema = yup.object({
  customer_id: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Customer")
    .required(),
  first_name: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Corporate Contact First Name")
    .required(),
  last_name: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Corporate Contact Last Name")
    .required(),
  phone_number: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .matches(/^\d{10}$/, "Must be a 10-digit number")
    .label("Corporate Contact Phone Number")
    .required(),
  email: yup
    .string()
    .email()
    .label("Corporate Contact Email")
    .lowercase("Please Enter Lowercase Letters")
    .required()
    .strict(),
});

const corporateContactUpdateSchema = yup.object({
  _id: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Corporate Contact")
    .required(),
  first_name: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Corporate Contact First Name")
    .required(),
  last_name: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Corporate Contact Last Name")
    .required(),
  phone_number: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .matches(/^\d{10}$/, "Must be a 10-digit number")
    .label("Corporate Contact Phone Number")
    .required(),
  email: yup
    .string()
    .email()
    .label("Corporate Contact Email")
    .lowercase("Please Enter Lowercase Letters")
    .required()
    .strict(),
});

const onboardingCustomerDetailsSchema = yup.object({
  customer_name: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Customer Name")
    .required(),
  project_overview: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Project Overview")
    .required(),
  address_line_1: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Address Line 1")
    .required(),
  address_line_2: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Address Line 2")
    .required(),
  city: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("City")
    .required(),
  state: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("State")
    .required(),
  country: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Counrty")
    .required(),
  zip_code: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Zip Code")
    .required(),
  time_zone: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Time Zone")
    .required(),
  contract_date: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Contract Date")
    .required(),
  effective_date: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Effective Date")
    .required(),
  solution: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Solution")
    .required(),
  corporate_contact_id: yup
    .object()
    .shape({ ...corporateContactSchema.fields }),
  number_of_properties: yup
    .number()
    .label("Number of Properties")
    .positive()
    .integer()
    .required(),
});

const onboardingBillingDetailsSchema = yup.object().shape({
  ...onboardingCustomerDetailsSchema.fields,
  billing_method: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Billing Method")
    .required(),
  billing_timeline: yup
    .string()
    .transform((value) => (value ? value.trim() : value))
    .label("Billing Timeline")
    .required(),
  billing_details: yup.object().when("billing_method", {
    is: BILLING_METHOD.CORPORATE,
    then: yup.object({
      address_line_1: yup
        .string()
        .transform((value) => (value ? value.trim() : value))
        .label("Address Line 1 ")
        .required(),
      address_line_2: yup
        .string()
        .transform((value) => (value ? value.trim() : value))
        .label("Address Line 2")
        .required(),
      city: yup
        .string()
        .transform((value) => (value ? value.trim() : value))
        .label("City")
        .required(),
      state: yup
        .string()
        .transform((value) => (value ? value.trim() : value))
        .label("State")
        .required(),
      country: yup
        .string()
        .transform((value) => (value ? value.trim() : value))
        .label("Country")
        .required(),
      zip_code: yup
        .string()
        .transform((value) => (value ? value.trim() : value))
        .label("Zip Code")
        .required(),
      name: yup
        .string()
        .transform((value) => (value ? value.trim() : value))
        .label("Billing Contact Name")
        .required(),
      phone_number: yup
        .string()
        .transform((value) => (value ? value.trim() : value))
        .matches(/^\d{10}$/, "Must be a 10-digit number")
        .label("Billing Contact Phone Number")
        .required(),
      email: yup
        .string()
        .email()
        .label("Billing Contact Email")
        .lowercase("Please Enter Lowercase Letters")
        .required()
        .strict(),
    }),
  }),
});

const onboardingPropertySchema = onboardingBillingDetailsSchema.concat(
  yup.object().shape({
    propertyData: yup
      .array()
      .of(
        yup.object().shape({
          ...onboardingPropertyDraftSchema.fields,
        }),
      )
      .test(
        "exceed-maximum-property-count",
        "Exceeded maximum no of properties allowed",
        (value, testContext) => {
          const { parent } = testContext;
          return value?.length <= parent?.number_of_properties;
        },
      ),
  }),
);
export {
  onboardingCustomerDetailsSchema,
  onboardingBillingDetailsSchema,
  onboardingPropertySchema,
  corporateContactSchema,
  corporateContactCreateSchema,
  corporateContactUpdateSchema,
};
